import { useEffect } from "react";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";
import { picUrl } from "@/lib/api";

/**
 * Global listener that toasts a "you got boosted" notification whenever the
 * backend pushes a `tip_received` event over the shared WebSocket.
 * Mount once (near the app root). No-op for logged-out users.
 */
export default function TipNotifier() {
  const { subscribe } = useWS();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return undefined;
    return subscribe((payload) => {
      if (!payload || payload.type !== "tip_received") return;
      const amt = (payload.amount_cents / 100).toFixed(2);
      const name = payload.tipper_name || "Anonymous";
      toast.custom(
        (id) => (
          <div
            data-testid="tip-notification"
            onClick={() => toast.dismiss(id)}
            className="flex items-start gap-3 min-w-[280px] max-w-sm bg-[#0A0A0C] border border-[#FF007F] shadow-[0_0_30px_rgba(255,0,127,0.5)] p-3 cursor-pointer"
          >
            <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center bg-[#FF007F]/20 border border-[#FF007F]/50">
              {payload.tipper_picture ? (
                <img
                  src={picUrl(payload.tipper_picture)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <Zap className="h-5 w-5 text-[#FF007F]" fill="#FF007F" strokeWidth={0} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm">
                <span className="font-bold">{name}</span>{" "}
                <span className="text-white/70">just boosted your video</span>
              </p>
              <p
                className="font-display font-black text-2xl neon-text-pink mt-0.5 leading-none glitch"
                data-text={`+$${amt}`}
              >
                +${amt}
              </p>
              {payload.message && (
                <p className="text-white/60 text-xs mt-1 italic truncate">
                  “{payload.message}”
                </p>
              )}
            </div>
          </div>
        ),
        { duration: 7000 }
      );
    });
  }, [subscribe, user]);

  return null;
}
